import { ergot, jwt, session } from '@bitstopco/bitstop-theme';
import type { AxiosResponse } from 'axios';

import {
  AUTH0_CLAIM_NAMESPACE,
  AUTH0_CONFIG,
  AUTH0_LOGIN_PATH,
  SESSION_ID_TOKEN_KEY,
} from '@/constants/app/config';

/**
 * Safe claim read: parseJwt throws on anything that is not base64url, and an
 * opaque or missing token is a normal state here rather than an error.
 *
 * @return {*} {Record<string, any>}
 */
const claims = (token?: string): Record<string, any> => {
  try {
    return token ? (jwt.parseJwt(token) ?? {}) : {};
  } catch {
    return {};
  }
};

export const isTokenExpired = (token?: string, skewMs = 0): boolean => {
  const { exp } = claims(token);

  return typeof exp === 'number' && exp * 1000 - skewMs <= Date.now();
};

/**
 * A custom claim arrives as whatever the post-login action put on it, and an
 * action reading an id out of app_metadata typically emits a string. The route
 * and sidebar guards compare operator_id to a number with `===`, so a string
 * fails every operator check silently — no error, just a redirect back to the
 * root. The user id is sent on as a note's author_id, which athena binds as an int.
 *
 * Only a positive integer or its plain digit string counts as an id. Number()
 * alone turns '  ', true and [] into 0 or 1, which would pass as a real id.
 *
 * @return {*} {number | undefined}
 */
const toNumericClaim = (value: unknown): number | undefined => {
  if (typeof value === 'number')
    return Number.isSafeInteger(value) && value > 0 ? value : undefined;

  if (typeof value === 'string' && /^\d+$/.test(value)) return toNumericClaim(Number(value));

  return undefined;
};

/**
 * Regroups Auth0 scopes into the permission map the dashboard checks against.
 *
 * Auth0 names a permission `{action}:{resource}` and hands them over space
 * delimited on the access token; ergot returns `{ resource: [actions] }`. OIDC
 * scopes (openid, profile, ...) carry no colon and drop out on the way through.
 *
 * @return {*} {Record<string, string[]>}
 */
export const groupPermissions = (scope?: string): Record<string, string[]> =>
  (scope ?? '').split(' ').reduce<Record<string, string[]>>((permissions, entry) => {
    if (!entry.includes(':')) return permissions;

    const [action, resource] = entry.split(':');
    if (!action || !resource) return permissions;

    permissions[resource] = [...(permissions[resource] ?? []), action];
    return permissions;
  }, {});

/**
 * Whether the stored session token was issued by our Auth0 tenant rather than by
 * ergot. Both are JWTs, so the issuer is what tells them apart.
 *
 * @return {*} {boolean}
 */
export const isAuth0Token = (token?: string): boolean =>
  !!AUTH0_CONFIG.domain && claims(token).iss === `https://${AUTH0_CONFIG.domain}/`;

export const storeAuth0Session = (accessToken: string, idToken?: string) => {
  session.setValue(accessToken);

  if (idToken) session.setValue(idToken, { key: SESSION_ID_TOKEN_KEY });
};

export const clearAuth0Session = () => {
  session.removeValue();
  session.removeValue({ key: SESSION_ID_TOKEN_KEY });
};

/**
 * Operator id out of whichever kind of token is in the session. ergot nests it in
 * the token's user object; Auth0 carries it as a namespaced custom claim.
 *
 * @return {*} {string | number | undefined}
 */
export const getOperatorIdFromToken = (token?: string) => {
  const payload = claims(token);

  return isAuth0Token(token)
    ? toNumericClaim(payload[`${AUTH0_CLAIM_NAMESPACE}/operator_id`])
    : payload?.user?.operator_id;
};

/**
 * Builds the userinfo payload from the Auth0 tokens instead of calling ergot.
 *
 * Everything the dashboard reads is already on the tokens the login exchange
 * returned — permissions in the access token's scope, operator and roles in the
 * namespaced claims, the display fields in the id token — so this is a pure
 * mapping with no request behind it. Shaped like ergot's introspection payload so
 * consumers cannot tell which login issued the session.
 *
 * Returned as a stand-in 200 response because it slots into the same action slot
 * as the real ergot call, which the theme types as an axios response.
 */
export const auth0UserInfo = async (): Promise<AxiosResponse> => {
  if (isTokenExpired(session.getValue())) {
    clearAuth0Session();

    throw new Error('The Auth0 session has expired.');
  }

  const accessToken = claims(session.getValue());
  const idToken = claims(session.getValue(SESSION_ID_TOKEN_KEY));

  const roles: string[] = accessToken[`${AUTH0_CLAIM_NAMESPACE}/roles`] ?? [];

  return {
    data: {
      // The legacy (ergot) user id the migration stored as internal_user_id, so
      // anything authored under either login lands on the same user.
      id: toNumericClaim(accessToken[`${AUTH0_CLAIM_NAMESPACE}/user_id`]),
      first_name: idToken.given_name,
      last_name: idToken.family_name,
      email: idToken.email,
      // ergot returns the single highest role as `role` and only fills `roles`
      // for multi-role applications; mirror both so either read works.
      role: roles.length ? { name: roles[0] } : undefined,
      roles: roles.map((name) => ({ name })),
      permissions: groupPermissions(accessToken.scope),
      context: {
        operator_id: toNumericClaim(accessToken[`${AUTH0_CLAIM_NAMESPACE}/operator_id`]),
      },
    },
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {},
  } as AxiosResponse;
};

export const handleExpiredAuth0Session = () => {
  const token = session.getValue();

  if (!isAuth0Token(token) || !isTokenExpired(token)) return;

  clearAuth0Session();

  window.location.assign(AUTH0_LOGIN_PATH);
};

/**
 * Action set handed to the theme's Authentication component as `customActions`.
 *
 * Only userinfo differs from the stock ergot actions, and it forks on the token
 * actually in the session, so /auth/login and /auth/login-v2 can both be live at
 * once without either knowing about the other.
 */
export const customAuthActions = {
  ...ergot,
  userInfo: () => (isAuth0Token(session.getValue()) ? auth0UserInfo() : ergot.userInfo()),
};
